'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion } from 'framer-motion';
import { Home, Briefcase, Image as ImageIcon, Calendar, Mail, Check } from 'lucide-react';

type SideItem = {
  href: string;
  icon: typeof Briefcase;
  label: string;
};

// Order: Work, Gallery, [Home FAB], Book, Contact
const leftItems: SideItem[] = [
  { href: '/projects', icon: Briefcase, label: 'Work' },
  { href: '/gallery', icon: ImageIcon, label: 'Gallery' },
];

const rightItems: SideItem[] = [
  { href: '/appointment', icon: Calendar, label: 'Book' },
  { href: '/contact', icon: Mail, label: 'Contact' },
];

export default function MobileNav() {
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  // Don't show on admin pages
  if (pathname.startsWith('/admin')) return null;

  const homeActive = isActive('/');

  const renderItem = (item: SideItem) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    return (
      <Link
        key={item.href}
        href={item.href}
        className="relative flex flex-col items-center justify-center gap-1 py-2 px-2 flex-1 min-w-0"
      >
        <motion.div
          animate={active ? { scale: 1.1, y: -2 } : { scale: 1, y: 0 }}
          whileTap={{ scale: 0.85 }}
          transition={{ type: 'spring' as const, stiffness: 320, damping: 20 }}
          className="flex items-center justify-center w-11 h-11"
        >
          <Icon
            size={22}
            className={
              active
                ? 'text-[#6C63FF] dark:text-[#B3ADFF]'
                : 'text-gray-400 dark:text-gray-500'
            }
          />
        </motion.div>
        <span
          className={`text-[10px] font-medium leading-none ${
            active
              ? 'text-[#6C63FF] dark:text-[#B3ADFF]'
              : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          {item.label}
        </span>
        {active && (
          <motion.span
            layoutId="mobile-nav-indicator"
            className="absolute -top-[1px] left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-full bg-gradient-to-r from-[#6C63FF] to-[#FF6584]"
            transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
          />
        )}
      </Link>
    );
  };

  return (
    <motion.nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 dark:bg-[#0b0821]/95 backdrop-blur-xl border-t border-black/5 dark:border-white/5"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring' as const, stiffness: 100, damping: 20 }}
    >
      <div className="relative flex items-end justify-between h-[70px] px-3">
        {/* Left group */}
        <div className="flex items-end flex-1 gap-1">
          {leftItems.map(renderItem)}
        </div>

        {/* Spacer for the central FAB */}
        <div className="w-20 shrink-0" aria-hidden="true" />

        {/* Right group */}
        <div className="flex items-end flex-1 gap-1">
          {rightItems.map(renderItem)}
        </div>

        {/* Central Home FAB — absolutely positioned so it pops out of the bar */}
        <Link
          href="/"
          aria-label="Home"
          className="absolute left-1/2 -translate-x-1/2 -top-7"
        >
          <motion.div
            className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#6C63FF] to-[#FF6584] shadow-[0_12px_24px_-8px_rgba(108,99,255,0.55)] ring-4 ring-white dark:ring-[#0b0821]"
            animate={{ scale: [1, 1.08, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            whileTap={{ scale: 0.9, transition: { type: 'spring' as const, stiffness: 500, damping: 15 } }}
          >
            <motion.div
              animate={homeActive ? { rotate: 8 } : { rotate: 0 }}
              transition={{ type: 'spring' as const, stiffness: 260, damping: 18 }}
              className="flex items-center justify-center"
            >
              <Home size={26} className="text-white" strokeWidth={2.5} />
            </motion.div>

            {homeActive && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring' as const, stiffness: 400, damping: 18 }}
                className="absolute -bottom-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full bg-white dark:bg-[#0b0821] shadow-md"
              >
                <Check size={12} className="text-[#6C63FF]" strokeWidth={3} />
              </motion.span>
            )}
          </motion.div>
        </Link>
      </div>
    </motion.nav>
  );
}
